import { closeCurrentModal } from 'weblite-web-modal-viewer'
import {
  GroupAdminChangeablePermissions,
  GroupMemberChangeablePermissions,
} from '@weblite/ts-action/subscription'
import {
  addAdmin,
  changeAdminPermissions,
  decodeGroupAdminPermissions,
  decodeSubscriptionPermissions,
  getAdminPermissions,
  removeAdmins,
} from '$logic/subscription'
import {
  GROUP_ADMIN_RIGHTS,
  HallPermissionsPage,
} from '$common/hall-permissions-page'
import { t } from '$i18n'
import { useAsyncFunction } from '$func/async-function.react-hook'
import { showSaveChangesLoadingModal } from '$comp/modals/loading-modal'
import {
  closeChannelAdminPermissions,
  closeGroupAdminPermissions,
} from '$logic/router'
import { useClientUserPermissions } from '$logic/subscription/react-hook'
import { changeHallOwner, getGroupChangeablePermission } from '$logic/hall'
import { resetOpenedUserList } from '$logic/opened-user-list'
import { showOperationFailedSnackbar } from '$comp/snackbars'
import { asyncDelay } from '$func/delay'

interface Props {
  userId: string
  hallId: string
  mode: 'DEFINE' | 'EDIT'
}

export function GroupAdminRights({ userId, hallId, mode }: Props) {
  const useAsyncFunctionCallBack =
    mode === 'DEFINE'
      ? () => decodeSubscriptionPermissions(hallId)
      : async () => {
          const adminPermission = await getAdminPermissions(hallId, userId)
          return decodeGroupAdminPermissions(hallId, adminPermission)
        }

  const { value: initialPermissions } =
    useAsyncFunction<GroupAdminChangeablePermissions>(
      useAsyncFunctionCallBack,
      [hallId, userId],
    )

  const { value: groupMemberPermissions } =
    useAsyncFunction<GroupMemberChangeablePermissions>(
      () => getGroupChangeablePermission(hallId),
      [hallId],
    )

  const clientUserPermissions = useClientUserPermissions(hallId)

  const onComplete = async (permissions: GroupAdminChangeablePermissions) => {
    showSaveChangesLoadingModal()
    if (mode === 'DEFINE') {
      analite('CHAT_ADD_ADMIN', { type: 'GROUP' })
      await addAdmin(hallId, userId, permissions)
    } else await changeAdminPermissions(hallId, userId, permissions)

    resetOpenedUserList()
    resetOpenedUserList('admins')

    await closeCurrentModal()
    await closeChannelAdminPermissions()
  }

  const onRemoveAdmin = async () => {
    showSaveChangesLoadingModal()

    const successful = await removeAdmins(hallId, [userId])
    if (!successful) {
      await closeCurrentModal()
      showOperationFailedSnackbar(() => removeAdmins(hallId, [userId]))

      return
    }

    resetOpenedUserList()
    resetOpenedUserList('admins')

    // Delay closing page to ensure previous pages are re-rendered
    await asyncDelay(0)

    await closeCurrentModal()
    await closeChannelAdminPermissions()
  }

  const onPromoteToOwner = async () => {
    await changeHallOwner(hallId, userId)

    resetOpenedUserList()
    resetOpenedUserList('admins')

    await closeChannelAdminPermissions()
  }

  return (
    <HallPermissionsPage
      confirmDisabled={false}
      rights={GROUP_ADMIN_RIGHTS.map(right => ({
        ...right,
        isLocked:
          !clientUserPermissions?.[right.id] ||
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          !!groupMemberPermissions?.[right.id],
      }))}
      actionButtonProps={
        mode !== 'DEFINE'
          ? {
              text: t('hallPermissionsPage.groupAdminRights.actionButtonText'),
              color: 'error',
            }
          : undefined
      }
      secondaryActionButtonProps={{
        text: t('hallPermissionsPage.groupAdminRights.secondaryButtonText'),
        color: 'secondary',
      }}
      pageTitle={t('hallPermissionsPage.groupAdminRights.title')}
      subTitle={t('hallPermissionsPage.groupAdminRights.subTitle')}
      userId={userId}
      initialPermissions={initialPermissions}
      onBack={closeGroupAdminPermissions}
      onComplete={onComplete}
      onActionButtonClick={onRemoveAdmin}
      onSecondaryActionButton={onPromoteToOwner}
    />
  )
}
